import { useEffect, useState, useCallback } from "react";
import { Save, Send, Info } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { CopyButton } from "@/components/CopyButton";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Card } from "@/components/ui/card";
import {
  Tabs, TabsContent, TabsList, TabsTrigger,
} from "@/components/ui/tabs";
import { toast } from "sonner";

function Section({ title, hint, children, testId }) {
  return (
    <Card className="mb-5 p-6 shadow-sm" data-testid={testId}>
      <div className="mb-4 flex items-start justify-between gap-4 border-b border-border pb-3">
        <div>
          <h2 className="font-heading text-base font-semibold tracking-tight">{title}</h2>
          {hint ? <p className="mt-1 text-xs text-muted-foreground">{hint}</p> : null}
        </div>
      </div>
      {children}
    </Card>
  );
}

export default function SettingsPage() {
  const [halo, setHalo] = useState(null);
  const [smtp, setSmtp] = useState(null);
  const [general, setGeneral] = useState(null);
  const [saving, setSaving] = useState({});
  const [testingSmtp, setTestingSmtp] = useState(false);

  const load = useCallback(async () => {
    try {
      const [h, s, g] = await Promise.all([
        api.get("/settings/halo"), api.get("/settings/smtp2go"), api.get("/settings/general"),
      ]);
      setHalo({ ...h.data, client_secret: "", admin_client_secret: "" });
      setSmtp({ ...s.data, api_key: "" });
      setGeneral({ ...g.data, _allowlist_str: (g.data.ip_allowlist || []).join(", ") });
    } catch (e) { toast.error(formatApiError(e)); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const saveHalo = async (e) => {
    e.preventDefault();
    setSaving((s) => ({ ...s, halo: true }));
    try {
      const body = { ...halo };
      if (!body.client_secret) delete body.client_secret;
      if (!body.admin_client_secret) delete body.admin_client_secret;
      const { data } = await api.post("/settings/halo", body);
      setHalo({ ...data, client_secret: "", admin_client_secret: "" });
      toast.success("HaloPSA settings saved");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving((s) => ({ ...s, halo: false })); }
  };

  const saveSmtp = async (e) => {
    e.preventDefault();
    setSaving((s) => ({ ...s, smtp: true }));
    try {
      const body = {
        ...smtp,
        alert_recipients: (smtp.alert_recipients || []).filter(Boolean),
      };
      if (!body.api_key) delete body.api_key;
      const { data } = await api.post("/settings/smtp2go", body);
      setSmtp({ ...data, api_key: "" });
      toast.success("SMTP2Go settings saved");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving((s) => ({ ...s, smtp: false })); }
  };

  const testSmtp = async () => {
    setTestingSmtp(true);
    try {
      const { data } = await api.post("/settings/smtp2go/test");
      (data.ok ? toast.success : toast.error)(data.message);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setTestingSmtp(false); }
  };

  const saveGeneral = async (e) => {
    e.preventDefault();
    setSaving((s) => ({ ...s, general: true }));
    try {
      const allowlist = (general._allowlist_str || "")
        .split(",").map((x) => x.trim()).filter(Boolean);
      const body = {
        bridge_public_url: general.bridge_public_url || "",
        ip_allowlist: allowlist,
        rate_limit_per_minute: Number(general.rate_limit_per_minute || 30),
        allow_direct_cpanel_username: !!general.allow_direct_cpanel_username,
      };
      const { data } = await api.post("/settings/general", body);
      setGeneral({ ...data, _allowlist_str: (data.ip_allowlist || []).join(", ") });
      toast.success("General settings saved");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving((s) => ({ ...s, general: false })); }
  };

  if (!halo || !smtp || !general) {
    return <div className="p-8 text-sm text-muted-foreground">Loading settings…</div>;
  }

  const buttonUrl = general.button_url_hint || "";

  return (
    <div>
      <PageHeader
        title="Settings"
        description="Halo credentials, SMTP2Go alerts, IP allow-list, and the exact custom-button URL to paste into HaloPSA."
        testId="settings-header"
      />

      <Tabs defaultValue="halo" className="w-full">
        <TabsList className="mb-5" data-testid="settings-tabs">
          <TabsTrigger value="halo" data-testid="tab-halo">HaloPSA</TabsTrigger>
          <TabsTrigger value="smtp" data-testid="tab-smtp">SMTP2Go Alerts</TabsTrigger>
          <TabsTrigger value="button" data-testid="tab-button">Custom Button</TabsTrigger>
          <TabsTrigger value="security" data-testid="tab-security">Security</TabsTrigger>
        </TabsList>

        {/* HALO TAB */}
        <TabsContent value="halo">
          <form onSubmit={saveHalo}>
            <Section title="HaloPSA API credentials" hint="Used to validate the calling user and (fallback) open provisioning tickets."
              testId="halo-section">
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <div>
                  <Label>Base URL</Label>
                  <Input value={halo.base_url}
                    onChange={(e) => setHalo({ ...halo, base_url: e.target.value })}
                    placeholder="https://your-halo.halopsa.com"
                    data-testid="halo-base-url-input" />
                </div>
                <div>
                  <Label>Tenant (optional)</Label>
                  <Input value={halo.tenant}
                    onChange={(e) => setHalo({ ...halo, tenant: e.target.value })}
                    data-testid="halo-tenant-input" />
                </div>
                <div>
                  <Label>Client ID</Label>
                  <Input value={halo.client_id}
                    onChange={(e) => setHalo({ ...halo, client_id: e.target.value })}
                    data-testid="halo-client-id-input" />
                </div>
                <div>
                  <Label>Client Secret {halo.has_client_secret ? <span className="text-xs text-muted-foreground">(stored, leave blank to keep)</span> : null}</Label>
                  <Input type="password" value={halo.client_secret}
                    onChange={(e) => setHalo({ ...halo, client_secret: e.target.value })}
                    placeholder={halo.has_client_secret ? "••••••" : ""}
                    data-testid="halo-client-secret-input" />
                  <p className="mt-1 text-xs text-muted-foreground">
                    From the Halo Application with <strong>Login Type: Authorisation Code</strong>.
                    Used for the portal SSO button (user identity handoff).
                  </p>
                </div>
                <div className="rounded-md border border-border/60 bg-muted/30 p-3">
                  <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                    Admin credentials (Client Credentials application)
                  </div>
                  <div className="grid grid-cols-1 gap-3">
                    <div>
                      <Label>Admin Client ID</Label>
                      <Input value={halo.admin_client_id || ""}
                        onChange={(e) => setHalo({ ...halo, admin_client_id: e.target.value })}
                        placeholder="Leave blank to reuse the primary Client ID"
                        data-testid="halo-admin-client-id-input" />
                    </div>
                    <div>
                      <Label>Admin Client Secret {halo.has_admin_client_secret ? <span className="text-xs text-muted-foreground">(stored, leave blank to keep)</span> : null}</Label>
                      <Input type="password" value={halo.admin_client_secret || ""}
                        onChange={(e) => setHalo({ ...halo, admin_client_secret: e.target.value })}
                        placeholder={halo.has_admin_client_secret ? "••••••" : "Leave blank to reuse the primary secret"}
                        data-testid="halo-admin-client-secret-input" />
                    </div>
                    <p className="text-xs text-muted-foreground">
                      From a <strong>second Halo Application</strong> with <strong>Login Type: Agent (Client Credentials)</strong>.
                      Used for Sync-from-Halo and provisioning-ticket creation. Halo requires a separate
                      application per login type; leave blank only if your primary app supports both flows.
                    </p>
                  </div>
                </div>
                <div>
                  <Label>Provisioning team ID</Label>
                  <Input type="number" value={halo.provisioning_team_id ?? ""}
                    onChange={(e) => setHalo({ ...halo, provisioning_team_id: e.target.value === "" ? null : Number(e.target.value) })}
                    placeholder="e.g. 4"
                    data-testid="halo-team-input" />
                </div>
                <div>
                  <Label>cPanel username field name <span className="text-xs text-muted-foreground">(Halo custom field system name)</span></Label>
                  <Input value={halo.cpanel_field_name || ""}
                    onChange={(e) => setHalo({ ...halo, cpanel_field_name: e.target.value })}
                    placeholder="CFcpanelusername"
                    data-testid="halo-cpanel-field-input" />
                  <p className="mt-1 text-xs text-muted-foreground">
                    Halo auto-prefixes custom-field system names with <code className="font-mono">CF</code>.
                    Use exactly the name Halo shows on the custom-field record (case sensitive).
                  </p>
                </div>
              </div>
            </Section>

            <Section title="Provisioning ticket template" hint="Used when a Halo user has no cPanel mapping. Available placeholders: {name}, {email}, {id}."
              testId="ticket-section">
              <div className="grid grid-cols-1 gap-4">
                <div>
                  <Label>Subject</Label>
                  <Input value={halo.ticket_subject_template}
                    onChange={(e) => setHalo({ ...halo, ticket_subject_template: e.target.value })}
                    data-testid="ticket-subject-input" />
                </div>
                <div>
                  <Label>Body</Label>
                  <Textarea rows={5} value={halo.ticket_body_template}
                    onChange={(e) => setHalo({ ...halo, ticket_body_template: e.target.value })}
                    data-testid="ticket-body-input" />
                </div>
              </div>
            </Section>

            <div className="flex justify-end">
              <Button type="submit" disabled={saving.halo} data-testid="halo-save-btn">
                <Save className="mr-1.5 h-4 w-4" />{saving.halo ? "Saving…" : "Save Halo settings"}
              </Button>
            </div>
          </form>
        </TabsContent>

        {/* SMTP TAB */}
        <TabsContent value="smtp">
          <form onSubmit={saveSmtp}>
            <Section title="SMTP2Go — failure alerts"
              hint="On every failed SSO handoff, we'll email these recipients. Get an API key at smtp2go.com → Settings → API Keys."
              testId="smtp-section">
              <div className="mb-4 flex items-center gap-3 rounded-md border border-border bg-muted/30 px-3 py-2 text-xs">
                <Switch
                  id="smtp-enabled"
                  checked={smtp.enabled}
                  onCheckedChange={(v) => setSmtp({ ...smtp, enabled: v })}
                  data-testid="smtp-enabled-switch"
                />
                <Label htmlFor="smtp-enabled" className="!m-0">Enable email alerts on failed SSO</Label>
              </div>
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <div>
                  <Label>API Key {smtp.has_api_key ? <span className="text-xs text-muted-foreground">(stored, leave blank to keep)</span> : null}</Label>
                  <Input type="password" value={smtp.api_key}
                    onChange={(e) => setSmtp({ ...smtp, api_key: e.target.value })}
                    placeholder={smtp.has_api_key ? "••••••" : "api-XXXXX"}
                    data-testid="smtp-key-input" />
                </div>
                <div>
                  <Label>From name</Label>
                  <Input value={smtp.from_name}
                    onChange={(e) => setSmtp({ ...smtp, from_name: e.target.value })}
                    data-testid="smtp-fromname-input" />
                </div>
                <div>
                  <Label>From address</Label>
                  <Input value={smtp.from_address}
                    onChange={(e) => setSmtp({ ...smtp, from_address: e.target.value })}
                    placeholder="alerts@yourmsp.com"
                    data-testid="smtp-fromaddr-input" />
                </div>
                <div>
                  <Label>Recipients (comma-separated)</Label>
                  <Input
                    value={(smtp.alert_recipients || []).join(", ")}
                    onChange={(e) => setSmtp({ ...smtp, alert_recipients: e.target.value.split(",").map((v) => v.trim()) })}
                    placeholder="ops@yourmsp.com, helpdesk@yourmsp.com"
                    data-testid="smtp-recipients-input"
                  />
                </div>
              </div>
            </Section>
            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={testSmtp} disabled={testingSmtp} data-testid="smtp-test-btn">
                <Send className="mr-1.5 h-4 w-4" />{testingSmtp ? "Sending…" : "Send test email"}
              </Button>
              <Button type="submit" disabled={saving.smtp} data-testid="smtp-save-btn">
                <Save className="mr-1.5 h-4 w-4" />{saving.smtp ? "Saving…" : "Save SMTP2Go"}
              </Button>
            </div>
          </form>
        </TabsContent>

        {/* CUSTOM BUTTON TAB */}
        <TabsContent value="button">
          <Section title="HaloPSA custom button URL"
            hint="Paste this URL as the target of a Self-Serve Portal custom button. This starts the secure OAuth flow — no user data in the URL."
            testId="button-section">
            <div className="rounded-md border border-border bg-muted/40 p-3 font-mono text-xs" data-testid="button-url-preview">
              {buttonUrl}
            </div>
            <div className="mt-3 flex items-center gap-2">
              <CopyButton value={buttonUrl} label="Copy URL" testId="copy-button-url-btn" />
            </div>
            <div className="mt-6 flex items-start gap-2 rounded-md border border-blue-500/30 bg-blue-500/5 p-3 text-xs text-blue-700 dark:text-blue-300">
              <Info className="mt-0.5 h-3.5 w-3.5 flex-shrink-0" />
              <div>
                In HaloPSA: <em>Configuration → Self-Service Portal → Custom Buttons</em>.
                Set the <strong>URL</strong> to the value above and enable <strong>Open in new tab</strong>.
                Also register <code>{(general.bridge_public_url || "").replace(/\/$/, "")}/api/sso/callback</code>
                {" "}as the redirect URI in your Halo OAuth application.
              </div>
            </div>
          </Section>

          <Section title="Bridge public URL"
            hint="The externally reachable HTTPS root of this bridge's BACKEND/API. In split-domain deployments this is the API host (e.g. api.sso.example.com), NOT the frontend admin host."
            testId="public-url-section">
            <form onSubmit={saveGeneral} className="space-y-3">
              <div>
                <Label>Public URL</Label>
                <Input value={general.bridge_public_url || ""}
                  onChange={(e) => setGeneral({ ...general, bridge_public_url: e.target.value })}
                  placeholder="https://sso-bridge.yourmsp.com"
                  data-testid="public-url-input" />
              </div>
              <div className="flex justify-end">
                <Button type="submit" disabled={saving.general} data-testid="public-url-save-btn">
                  <Save className="mr-1.5 h-4 w-4" />{saving.general ? "Saving…" : "Save"}
                </Button>
              </div>
            </form>
          </Section>
        </TabsContent>

        {/* SECURITY TAB */}
        <TabsContent value="security">
          <Section title="IP allow-list & rate limiting"
            hint="Restrict who can call /sso/cpanel. Leave the allow-list empty to allow any source IP."
            testId="security-section">
            <form onSubmit={saveGeneral} className="space-y-4">
              <div>
                <Label>Allowed source IPs (comma-separated)</Label>
                <Input
                  value={general._allowlist_str || ""}
                  onChange={(e) => setGeneral({ ...general, _allowlist_str: e.target.value })}
                  placeholder="203.0.113.5, 198.51.100.10"
                  data-testid="allowlist-input"
                />
                <p className="mt-1 text-xs text-muted-foreground">
                  Currently: {general.ip_allowlist?.length ? general.ip_allowlist.join(", ") : "none (allow all)"}
                </p>
              </div>
              <div>
                <Label>Rate limit (requests per minute per IP)</Label>
                <Input
                  type="number" min={1} max={600}
                  value={general.rate_limit_per_minute}
                  onChange={(e) => setGeneral({ ...general, rate_limit_per_minute: e.target.value })}
                  className="w-32"
                  data-testid="ratelimit-input"
                />
              </div>
              <div className="rounded-md border border-amber-500/30 bg-amber-500/5 p-3">
                <label className="flex items-start gap-3 text-sm">
                  <input
                    type="checkbox"
                    className="mt-0.5 h-4 w-4 accent-amber-600"
                    checked={!!general.allow_direct_cpanel_username}
                    onChange={(e) => setGeneral({ ...general, allow_direct_cpanel_username: e.target.checked })}
                    data-testid="allow-direct-cpanel-toggle"
                  />
                  <span>
                    <span className="font-semibold text-amber-800 dark:text-amber-300">
                      Allow legacy direct <code>cpanel_username</code> URL parameter (INSECURE)
                    </span>
                    <span className="mt-1 block text-xs text-muted-foreground">
                      When OFF (recommended), <code>/api/sso/cpanel?cpanel_username=…</code> is rejected.
                      Only the OAuth flow (<code>/api/sso/oauth-start</code>) is accepted for public use.
                      Turn this ON only if you understand a user could alter the URL to sign into
                      another cPanel account, and you have locked the endpoint behind the IP allow-list.
                    </span>
                  </span>
                </label>
              </div>
              <div className="flex justify-end">
                <Button type="submit" disabled={saving.general} data-testid="security-save-btn">
                  <Save className="mr-1.5 h-4 w-4" />{saving.general ? "Saving…" : "Save security settings"}
                </Button>
              </div>
            </form>
          </Section>
        </TabsContent>
      </Tabs>
    </div>
  );
}
